import React, { useRef, useState } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Icon from '@amzn/meridian/icon';
import infoIcon from '@amzn/meridian-tokens/base/icon/info';
import chevronDownIcon from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import CustomAlert from '../../commonComponents/CustomAlert';
import CopyToClipboardButton from '../../commonComponents/CopyToClipboardButton';
import { PLAN_STATUS, PLAN_VERSIONS, TEXTS } from '../../utils/constants';
import { ALERT_BG_WARNING, ALERT_MESSAGES } from './flowPlannerConstants';

const EXPERIMENTATION_BANNER = (
  <CustomAlert backgroundColor={ALERT_BG_WARNING} title="You are on Flow Planner experimentation mode." />
);

const ALERT_CONFIG = {
  [PLAN_STATUS.PENDING_APPROVAL]: { bg: ALERT_BG_WARNING, title: ALERT_MESSAGES.AWAITING_APPROVAL },
  [PLAN_STATUS.PENDING_USER_INPUT]: { bg: ALERT_BG_WARNING, title: ALERT_MESSAGES.AWAITING_APPROVAL },
  [PLAN_STATUS.APPROVED]: { title: ALERT_MESSAGES.APPROVED },
  [PLAN_STATUS.REJECTED]: { bg: ALERT_BG_WARNING, title: ALERT_MESSAGES.REJECTED },
  [PLAN_STATUS.FAILED]: { bg: ALERT_BG_WARNING, title: ALERT_MESSAGES.CREATION_FAILED }
};

// A plan exists (so all planning inputs are available) for exactly the statuses
// that get a completed-plan alert. Shared so Settings shows the same set of inputs.
export const planHasRun = (planStatus) => !!ALERT_CONFIG[planStatus];

export default function PlanStatusAlert({
  planStatus,
  error,
  planId,
  planVersion,
  onRunPlan,
  onReplan,
  onOpenOptimizer,
  onOpenSettings,
  onCalculatePlan,
  onUpdatePlan,
  onApprovePlan,
  timeDistributions = null,
  layout = 'new',
  isExperimentationMode = false
}) {
  // Classic keeps the original inline alert: Time Distributions + Re-run Plan, with
  // Calculate/Approve living in the on-page button row instead of the bar.
  const isClassic = layout === 'classic';
  const menuAnchor = useRef();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const MODEL_TYPE_LABELS = {
    [PLAN_VERSIONS.DYNAMIC_V2]: 'Math Optimizer',
    [PLAN_VERSIONS.STATIC_V3]: 'Heuristic'
  };
  const modelTypeLabel = MODEL_TYPE_LABELS[planVersion] || null;
  const subtitle = planId ? (
    <React.Fragment>
      {`(Plan ID: ${planId}`}
      <CopyToClipboardButton value={planId} />
      {`${modelTypeLabel ? `, Model Type: ${modelTypeLabel}` : ''})`}
    </React.Fragment>
  ) : (
    ''
  );

  const sticky = (content) => (isClassic ? content : <div className="fp-status-bar-sticky">{content}</div>);

  const withExperimentation = (content) =>
    isExperimentationMode ? (
      <Column spacing="200" width="100%">
        {EXPERIMENTATION_BANNER}
        {content}
      </Column>
    ) : (
      content
    );

  if (planStatus === PLAN_STATUS.PENDING_CREATION) {
    return withExperimentation(
      <CustomAlert backgroundColor="highlight" title={ALERT_MESSAGES.GENERATING} subtitle={subtitle}>
        <Row spacing="200" alignmentVertical="center">
          <Loader size="small" />
        </Row>
      </CustomAlert>
    );
  }

  const settingsButton = onOpenSettings ? (
    <Button type="secondary" size="small" onClick={onOpenSettings}>
      Inputs
    </Button>
  ) : null;

  // Inside the shift the primary action becomes Update Plan and Calculate Plan moves
  // into the dropdown alongside Reset Plan.
  const runMenuItem = (label, action) => (
    <MenuItem
      onClick={() => {
        setIsMenuOpen(false);
        action();
      }}>
      {label}
    </MenuItem>
  );

  const calculatePlanButton = (
    <React.Fragment>
      <span className="fp-split-button" ref={menuAnchor}>
        <Button size="small" onClick={onUpdatePlan || onCalculatePlan}>
          {onUpdatePlan ? 'Update Plan' : 'Calculate Plan'}
        </Button>
        <Button size="small" onClick={() => setIsMenuOpen(true)} aria-label="More plan options">
          <Icon tokens={chevronDownIcon} />
        </Button>
      </span>
      <Menu
        anchorNode={menuAnchor.current}
        open={isMenuOpen}
        position="bottom"
        alignment="end"
        onClose={() => setIsMenuOpen(false)}>
        {onUpdatePlan && runMenuItem('Calculate Plan', onCalculatePlan)}
        {runMenuItem('Reset Plan', onReplan)}
      </Menu>
    </React.Fragment>
  );

  const config = ALERT_CONFIG[planStatus];
  if (!config) {
    return sticky(
      withExperimentation(
        <CustomAlert title={ALERT_MESSAGES.NOT_RUN}>
          <Row spacing="200" alignmentHorizontal="end">
            {isClassic ? timeDistributions : settingsButton}
            <Button size="small" onClick={onRunPlan}>
              Run Plan
            </Button>
            {onOpenOptimizer && (
              <Button type="secondary" size="small" onClick={onOpenOptimizer}>
                Optimizer
              </Button>
            )}
          </Row>
        </CustomAlert>
      )
    );
  }

  return sticky(
    withExperimentation(
      <Box
        backgroundColor={config.bg || 'highlight'}
        type="fill"
        spacingInset="300"
        width="100%"
        className="alert-border">
        <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
          <Row spacingInset="200" alignmentVertical="top">
            <Icon tokens={infoIcon} />
            <Column spacing="100">
              <Text>{error || config.title}</Text>
              <Text type={TEXTS.T4}>{<strong>{subtitle}</strong>}</Text>
            </Column>
          </Row>
          <Row spacing="200" alignmentVertical="center">
            {isClassic ? (
              <React.Fragment>
                {timeDistributions}
                <Button size="small" onClick={onReplan}>
                  Re-run Plan
                </Button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {settingsButton}
                {calculatePlanButton}
                {onApprovePlan && (
                  <Button type="primary" size="small" onClick={onApprovePlan}>
                    Approve
                  </Button>
                )}
              </React.Fragment>
            )}
          </Row>
        </Row>
      </Box>
    )
  );
}
